import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const API = 'https://green-energy.com.co/api';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [chargers, setChargers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = async () => {
    try {
      const [statsRes, chargersRes, txsRes] = await Promise.all([
        fetch(`${API}/stats`).then(r=>r.json()),
        fetch(`${API}/chargers`).then(r=>r.json()),
        fetch(`${API}/transactions`).then(r=>r.json()),
      ]);
      setStats(statsRes);
      setChargers(chargersRes);
      setTxs(txsRes);
      setLastUpdate(new Date());
      setLoading(false);
    } catch(e) { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 30000); // actualiza cada 30 seg
    return () => clearInterval(iv);
  }, []);

  if (loading) return <div style={{padding:'3rem',textAlign:'center',color:'#666'}}>Cargando dashboard...</div>;

  // Sesiones de hoy
  const hoy = new Date().toISOString().slice(0,10);
  const sesionesHoy = txs.filter(t => (t.start_time||t.startTime)?.startsWith(hoy));
  const energiaHoy = sesionesHoy.reduce((a,t)=>a+(t.energy_delivered||t.energyDelivered||0),0);

  // Sesiones de ayer
  const ayer = new Date(Date.now()-86400000).toISOString().slice(0,10);
  const sesionesAyer = txs.filter(t => (t.start_time||t.startTime)?.startsWith(ayer));
  const energiaAyer = sesionesAyer.reduce((a,t)=>a+(t.energy_delivered||t.energyDelivered||0),0);

  // Gráfica energía por día (últimos 14 días)
  const energiaPorDia = [];
  for (let i=13; i>=0; i--) {
    const d = new Date(Date.now() - i*86400000);
    const dStr = d.toISOString().slice(0,10);
    const energia = txs
      .filter(t => (t.start_time||t.startTime)?.startsWith(dStr))
      .reduce((a,t)=>a+(t.energy_delivered||t.energyDelivered||0),0);
    energiaPorDia.push({
      dia: `${d.getDate()}/${d.getMonth()+1}`,
      energia,
      sesiones: txs.filter(t=>(t.start_time||t.startTime)?.startsWith(dStr)).length
    });
  }

  // Estado de cargadores
  const disponibles = chargers.filter(c=>c.status==='Available').length;
  const cargando = chargers.filter(c=>c.status==='Charging').length;
  const faulted = chargers.filter(c=>c.status==='Faulted').length;
  const offline = chargers.filter(c=>c.status==='Offline').length;
  const totalCargadores = chargers.length;

  // Por COP
  const mf = chargers.filter(c=>c.cop==='Fontibón');
  const mu = chargers.filter(c=>c.cop==='Usme');

  // Top buses más activos
  const porBus = {};
  txs.forEach(t => {
    const key = t.zonal || t.vin || t.id_tag || t.idTag || 'Desconocido';
    if (!porBus[key]) porBus[key] = { sesiones:0, energia:0 };
    porBus[key].sesiones++;
    porBus[key].energia += t.energy_delivered || t.energyDelivered || 0;
  });
  const topBuses = Object.entries(porBus)
    .sort((a,b)=>b[1].energia-a[1].energia)
    .slice(0,5)
    .map(([zonal,d])=>({zonal,...d}));

  const socColor = s => s>=75?'#4ade80':s>=40?'#60a5fa':'#f59e0b';

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem'}}>
        <div className="page-title" style={{margin:0}}>Dashboard — Electric Fleet Management</div>
        {lastUpdate && (
          <div style={{fontSize:'12px',color:'#555'}}>
            Actualizado: {lastUpdate.toLocaleTimeString('es-CO')}
            <button onClick={fetchData} style={{marginLeft:'8px',padding:'3px 8px',background:'#2a2d3a',border:'none',borderRadius:'5px',color:'#aaa',cursor:'pointer',fontSize:'11px'}}>
              🔄 Refrescar
            </button>
          </div>
        )}
      </div>

      {/* Métricas principales */}
      <div className="metrics">
        <div className="metric-card">
          <div className="metric-label">Sesiones hoy</div>
          <div className="metric-value">{sesionesHoy.length}</div>
          <div className="metric-sub" style={{color:sesionesHoy.length>=sesionesAyer.length?'#4ade80':'#f87171'}}>
            {sesionesAyer.length > 0 ? `${sesionesHoy.length >= sesionesAyer.length?'↑':'↓'} vs ayer (${sesionesAyer.length})` : 'Sin data de ayer'}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Energía hoy</div>
          <div className="metric-value">{energiaHoy.toLocaleString()} kWh</div>
          <div className="metric-sub" style={{color:energiaHoy>=energiaAyer?'#4ade80':'#f87171'}}>
            {energiaAyer > 0 ? `${energiaHoy>=energiaAyer?'↑':'↓'} vs ayer (${energiaAyer.toLocaleString()} kWh)` : 'Sin data de ayer'}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Total sesiones históricas</div>
          <div className="metric-value">{(stats?.totalSessions||txs.length).toLocaleString()}</div>
          <div className="metric-sub">en base de datos</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Energía total histórica</div>
          <div className="metric-value">{((stats?.totalEnergy||0)/1000).toFixed(1)} MWh</div>
          <div className="metric-sub">desde inicio</div>
        </div>
      </div>

      {/* Gráficas */}
      <div className="grid2">
        <div className="card">
          <div className="card-title">Energía entregada por día — últimos 14 días (kWh)</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={energiaPorDia}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="dia" tick={{fill:'#666',fontSize:10}} />
              <YAxis tick={{fill:'#666',fontSize:11}} />
              <Tooltip contentStyle={{background:'#1a1d27',border:'1px solid #2a2d3a',color:'#fff'}} formatter={v=>`${v} kWh`} />
              <Bar dataKey="energia" fill="#4ade80" radius={[3,3,0,0]} name="Energía kWh" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <div className="card-title">Sesiones por día — últimos 14 días</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={energiaPorDia}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="dia" tick={{fill:'#666',fontSize:10}} />
              <YAxis tick={{fill:'#666',fontSize:11}} />
              <Tooltip contentStyle={{background:'#1a1d27',border:'1px solid #2a2d3a',color:'#fff'}} />
              <Line type="monotone" dataKey="sesiones" stroke="#60a5fa" strokeWidth={2} dot={false} name="Sesiones" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Estado cargadores conectados */}
      {totalCargadores > 0 && (
        <div className="grid2">
          <div className="card">
            <div className="card-title">Estado de cargadores en vivo</div>
            <div style={{display:'flex',flexDirection:'column',gap:'10px',marginTop:'8px'}}>
              {[
                {label:'Disponibles', value:disponibles, total:totalCargadores, color:'#4ade80'},
                {label:'Cargando', value:cargando, total:totalCargadores, color:'#60a5fa'},
                {label:'Con falla', value:faulted, total:totalCargadores, color:'#f87171'},
                {label:'Sin red', value:offline, total:totalCargadores, color:'#888'},
              ].map(item => (
                <div key={item.label}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',marginBottom:'4px'}}>
                    <span style={{color:'#aaa'}}>{item.label}</span>
                    <span style={{color:'#fff',fontWeight:'500'}}>{item.value} / {item.total}</span>
                  </div>
                  <div className="soc-bar-bg">
                    <div className="soc-bar-fill" style={{width:`${(item.value/item.total)*100}%`,background:item.color}}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-title">Cargadores en vivo</div>
            <div style={{display:'flex',flexDirection:'column',gap:'6px',maxHeight:'200px',overflowY:'auto'}}>
              {chargers.map(c => (
                <div key={c.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'6px 0',borderBottom:'1px solid #1e2130'}}>
                  <span className="badge" style={{fontSize:'10px'}}>{c.id}</span>
                  <span style={{fontSize:'11px',color:'#666',flex:1}}>{c.cop}</span>
                  {c.soc !== null && c.soc !== undefined && (
                    <span style={{fontSize:'11px',color:socColor(c.soc),fontWeight:'500'}}>{c.soc}%</span>
                  )}
                  <span style={{fontSize:'11px',
                    color:c.status==='Available'?'#4ade80':c.status==='Charging'?'#60a5fa':c.status==='Faulted'?'#f87171':'#888'}}>
                    {c.status==='Available'?'Libre':c.status==='Charging'?'Cargando':c.status==='Faulted'?'Falla':'Sin red'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* COP cards */}
      <div className="grid2">
        <div className="card">
          <div className="card-title">Mueve Fontibón</div>
          <div style={{fontSize:'28px',fontWeight:'700',color:'#fff',margin:'0.5rem 0'}}>FONTIBÓN</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px',fontSize:'13px'}}>
            <div style={{color:'#aaa'}}>Cargadores registrados</div><div>81</div>
            <div style={{color:'#aaa'}}>Conectados ahora</div><div style={{color:'#4ade80'}}>{mf.length}</div>
            <div style={{color:'#aaa'}}>Sesiones históricas</div><div>{txs.filter(t=>t.cop==='Fontibón').length}</div>
            <div style={{color:'#aaa'}}>Energía total</div><div>{txs.filter(t=>t.cop==='Fontibón').reduce((a,t)=>a+(t.energy_delivered||0),0).toLocaleString()} kWh</div>
          </div>
        </div>
        <div className="card">
          <div className="card-title">Mueve Usme</div>
          <div style={{fontSize:'28px',fontWeight:'700',color:'#fff',margin:'0.5rem 0'}}>USME</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px',fontSize:'13px'}}>
            <div style={{color:'#aaa'}}>Cargadores registrados</div><div>108</div>
            <div style={{color:'#aaa'}}>Conectados ahora</div><div style={{color:'#4ade80'}}>{mu.length}</div>
            <div style={{color:'#aaa'}}>Sesiones históricas</div><div>{txs.filter(t=>t.cop==='Usme').length}</div>
            <div style={{color:'#aaa'}}>Energía total</div><div>{txs.filter(t=>t.cop==='Usme').reduce((a,t)=>a+(t.energy_delivered||0),0).toLocaleString()} kWh</div>
          </div>
        </div>
      </div>

      {/* Top buses */}
      {topBuses.length > 0 && (
        <div className="card">
          <div className="card-title">Top 5 buses por energía recibida</div>
          <table>
            <thead>
              <tr><th>#</th><th>Bus / Zonal</th><th>Sesiones</th><th>Energía total (kWh)</th><th>Promedio por sesión</th></tr>
            </thead>
            <tbody>
              {topBuses.map((b,i) => (
                <tr key={b.zonal}>
                  <td style={{color:'#555'}}>{i+1}</td>
                  <td><span style={{color:'#4ade80',fontWeight:'500'}}>{b.zonal}</span></td>
                  <td style={{color:'#aaa'}}>{b.sesiones}</td>
                  <td style={{fontWeight:'500'}}>{b.energia.toLocaleString()}</td>
                  <td style={{color:'#aaa'}}>{Math.round(b.energia/b.sesiones)} kWh</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
